import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessagesSquare, Map, PenTool, Code2, ShieldCheck, Rocket } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const STEPS = [
  { icon: MessagesSquare, t: "Consultation", d: "Understand business requirements." },
  { icon: Map, t: "Planning", d: "Create website structure and design concepts." },
  { icon: PenTool, t: "Design", d: "Develop beautiful and engaging website layouts." },
  { icon: Code2, t: "Development", d: "Build a fast, responsive website." },
  { icon: ShieldCheck, t: "Testing", d: "Check responsiveness, performance, and functionality." },
  { icon: Rocket, t: "Launch", d: "Publish the approved website." },
];

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 60%"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="process" data-testid="process-section" className="relative overflow-hidden bg-navy/40 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Our Website Development Process" title={<>From first chat to <span className="text-gold-grad italic">launch day</span>.</>} />
        <div ref={ref} className="relative mt-20">
          <div className="absolute left-[27px] top-0 h-full w-px bg-white/10 lg:left-0 lg:top-[27px] lg:h-px lg:w-full" />
          <motion.div style={{ scaleY: scale }} className="absolute left-[27px] top-0 h-full w-px origin-top bg-gradient-to-b from-[var(--gold)] to-[var(--royal)] lg:hidden" />
          <motion.div style={{ scaleX: scale }} className="absolute left-0 top-[27px] hidden h-px w-full origin-left bg-gradient-to-r from-[var(--gold)] to-[var(--royal)] lg:block" />
          <ol className="grid gap-12 lg:grid-cols-6 lg:gap-6">
            {STEPS.map((s, i) => (
              <Reveal as="li" key={s.t} delay={i * 0.08} className="relative flex gap-6 lg:block">
                <div data-testid={`process-step-${i}`} className="group contents">
                  <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-[var(--ink)] text-[var(--gold)] transition-[background-color,color,transform] duration-500 group-hover:scale-110 group-hover:bg-[var(--gold)] group-hover:text-black">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <div className="lg:mt-8">
                    <p className="font-display text-sm font-medium text-slate-400">Step {String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-display mt-1 text-xl font-semibold text-white">{s.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
